import { Component } from '@theme/component';
import { STORE_DATA } from './zendesk-store-data.js';

const TICKET_FORM_ID = '17036371195535';

const STATE_FIELD_ID = '17027077736591';
const ENQUIRY_TYPE_FIELD_ID = '17027959545615';
const FIRST_NAME_FIELD_ID = '17026816974863';
const LAST_NAME_FIELD_ID = '17026807998095';
const EMAIL_FIELD_ID = '17026899326223';
const PHONE_FIELD_ID = '17026909763471';
const ORDER_NUMBER_FIELD_ID = '17432770667535';
const REASON_FOR_RETURN_FIELD_ID = '17028300017295';

const RETURN_ENQUIRY_TYPE_VALUE =
  'i_want_to_request_a_refund_or_return_for_an_order_purchased_online';


/**
 * @typedef {object} ZendeskContactFormRefs
 * @property {HTMLFormElement} [form]
 * @property {HTMLElement} [errorMessage]
 * @property {HTMLElement} [successMessage]
 * @property {HTMLSelectElement} [stateSelect]
 * @property {HTMLSelectElement} [storeSelect]
 * @property {HTMLSelectElement} [enquiryTypeSelect]
 * @property {HTMLElement} [returnFieldsRow]
 */

/**
 * Submits contact form data to the Zendesk Requests API, targeting the
 * "Contact us" ticket form (17036371195535).
 *
 * @extends {Component<ZendeskContactFormRefs>}
 */
class ZendeskContactForm extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {AbortController | null} */
  #fieldsAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#populateStateOptions();
    this.refs.form?.addEventListener('submit', this.#handleSubmit);
    this.refs.stateSelect?.addEventListener('change', this.#handleStateChange);
    this.refs.enquiryTypeSelect?.addEventListener('change', this.#handleEnquiryTypeChange);
    this.#bindFieldLabelStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form?.removeEventListener('submit', this.#handleSubmit);
    this.refs.stateSelect?.removeEventListener('change', this.#handleStateChange);
    this.refs.enquiryTypeSelect?.removeEventListener('change', this.#handleEnquiryTypeChange);
    this.#activeFetch?.abort();
    this.#fieldsAbortController?.abort();
  }

  #populateStateOptions() {
    const select = this.refs.stateSelect;
    if (!(select instanceof HTMLSelectElement)) return;

    for (const [value, data] of Object.entries(STORE_DATA)) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = data.label;
      select.append(option);
    }
  }

  #handleStateChange = () => {
    const select = this.refs.stateSelect;
    const storeSelect = this.refs.storeSelect;

    if (!(select instanceof HTMLSelectElement) || !(storeSelect instanceof HTMLSelectElement)) {
      return;
    }

    const stateValue = select.value;
    const data = STORE_DATA[stateValue];

    storeSelect.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.disabled = true;
    placeholder.selected = true;
    placeholder.textContent = storeSelect.dataset.placeholder || 'Select a Store';
    storeSelect.append(placeholder);

    if (data) {
      for (const option of data.options) {
        const optionEl = document.createElement('option');
        optionEl.value = option.value;
        optionEl.textContent = option.label;
        storeSelect.append(optionEl);
      }
    }
  };

  #handleEnquiryTypeChange = () => {
    const select = this.refs.enquiryTypeSelect;
    const returnFieldsRow = this.refs.returnFieldsRow;

    if (!(select instanceof HTMLSelectElement) || !(returnFieldsRow instanceof HTMLElement)) {
      return;
    }

    returnFieldsRow.hidden = select.value !== RETURN_ENQUIRY_TYPE_VALUE;
  };

  /**
   * Labels are muted until the field is focused or has a value.
   */
  #bindFieldLabelStates() {
    this.#fieldsAbortController?.abort();
    this.#fieldsAbortController = new AbortController();
    const { signal } = this.#fieldsAbortController;

    const fields = this.querySelectorAll('.zendesk-contact-form__field');

    for (const field of fields) {
      if (!(field instanceof HTMLElement)) continue;

      const control = field.querySelector('input, select, textarea');
      if (
        !(control instanceof HTMLInputElement) &&
        !(control instanceof HTMLSelectElement) &&
        !(control instanceof HTMLTextAreaElement)
      ) {
        continue;
      }

      const sync = () => {
        const hasValue = control.value.trim().length > 0;
        const isFocused = document.activeElement === control;
        field.classList.toggle('is-active', hasValue || isFocused);
      };

      control.addEventListener('focus', sync, { signal });
      control.addEventListener('blur', sync, { signal });
      control.addEventListener('input', sync, { signal });
      control.addEventListener('change', sync, { signal });
      sync();
    }
  }

  /**
   * Normalises an Australian mobile number to "04XX XXX XXX".
   * Returns null if the digits don't form a valid AU mobile number.
   * @param {string} rawPhone
   * @returns {string | null}
   */
  #formatPhone(rawPhone) {
    const digits = rawPhone.replace(/\D/g, '');
    if (!/^04\d{8}$/.test(digits)) return null;
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = this.refs.form;
    if (!(form instanceof HTMLFormElement)) return;

    this.#hideMessages();

    if (!form.checkValidity()) {
      const invalidField = form.querySelector(':invalid');

      if (invalidField instanceof HTMLElement) {
        invalidField.focus();
        const labelledField =
          invalidField instanceof HTMLInputElement ||
          invalidField instanceof HTMLSelectElement ||
          invalidField instanceof HTMLTextAreaElement
            ? invalidField
            : null;
        const fieldLabel = labelledField?.labels?.[0]?.textContent?.trim();

        this.#showError(
          fieldLabel
            ? `Please complete all required fields. Check ${fieldLabel}.`
            : 'Please complete all required fields.'
        );
      } else {
        this.#showError('Please complete all required fields.');
      }

      form.reportValidity();
      return;
    }

    const formData = new FormData(form);
    const firstName = String(formData.get('first_name') ?? '').trim();
    const lastName = String(formData.get('last_name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const confirmEmail = String(formData.get('confirm_email') ?? '').trim();
    const phone = String(formData.get('phone') ?? '').trim();
    const state = String(formData.get('state') ?? '').trim();
    const store = String(formData.get('store') ?? '').trim();
    const enquiryType = String(formData.get('enquiry_type') ?? '').trim();
    const orderNumber = String(formData.get('order_number') ?? '').trim();
    const reasonForReturn = String(formData.get('reason_for_return') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();

    if (email !== confirmEmail) {
      this.#showError(this.dataset.emailMismatchMessage || 'Email addresses do not match.');
      return;
    }

    let formattedPhone = '';
    if (phone) {
      formattedPhone = this.#formatPhone(phone) ?? '';
      if (!formattedPhone) {
        this.#showError(
          this.dataset.phoneInvalidMessage ||
            'Please enter a valid mobile number, e.g. 0412 345 678.'
        );
        return;
      }
    }

    const storeData = STORE_DATA[state];
    if (!storeData) {
      this.#showError('Please select a valid state.');
      return;
    }

    const isReturnEnquiry = enquiryType === RETURN_ENQUIRY_TYPE_VALUE;
    if (isReturnEnquiry && (!orderNumber || !reasonForReturn)) {
      this.#showError('Please provide your order number and reason for return.');
      return;
    }

    const subdomain = this.dataset.subdomain?.trim();
    const endpointOverride = this.dataset.endpoint?.trim();
    const endpoint =
      endpointOverride ||
      (subdomain ? `https://${subdomain}.zendesk.com/api/v2/requests.json` : '');

    if (!endpoint) {
      this.#showError(
        this.dataset.configurationErrorMessage ||
          'Contact form is not configured. Add a Zendesk subdomain in theme settings.'
      );
      return;
    }

    const enquiryTypeLabel = this.#getSelectedLabel(this.refs.enquiryTypeSelect);
    const subjectPrefix = this.dataset.subjectPrefix || 'Website Enquiry';
    const subject = enquiryTypeLabel ? `${subjectPrefix}: ${enquiryTypeLabel}` : subjectPrefix;
    const requesterName = [firstName, lastName].filter(Boolean).join(' ').trim() || email;

    const commentBody = [
      `First Name: ${firstName}`,
      `Last Name: ${lastName}`,
      `Email: ${email}`,
      `Phone: ${formattedPhone}`,
      `State: ${storeData.label}`,
      `Store: ${this.#getSelectedLabel(this.refs.storeSelect)}`,
      `Enquiry Type: ${enquiryTypeLabel}`,
      ...(isReturnEnquiry
        ? [`Order Number: ${orderNumber}`, `Reason for Return: ${reasonForReturn}`]
        : []),
      '',
      'Message:',
      message,
    ].join('\n');

    const customFields = [
      { id: Number(STATE_FIELD_ID), value: state },
      { id: Number(storeData.fieldId), value: store },
      { id: Number(ENQUIRY_TYPE_FIELD_ID), value: enquiryType },
      { id: Number(FIRST_NAME_FIELD_ID), value: firstName },
      { id: Number(LAST_NAME_FIELD_ID), value: lastName },
      { id: Number(EMAIL_FIELD_ID), value: email },
    ];

    if (formattedPhone) {
      customFields.push({ id: Number(PHONE_FIELD_ID), value: formattedPhone });
    }

    if (isReturnEnquiry) {
      customFields.push({ id: Number(ORDER_NUMBER_FIELD_ID), value: orderNumber });
      customFields.push({ id: Number(REASON_FOR_RETURN_FIELD_ID), value: reasonForReturn });
    }

    const payload = {
      request: {
        requester: {
          name: requesterName,
          email,
        },
        subject,
        comment: {
          body: commentBody,
        },
        ticket_form_id: Number(TICKET_FORM_ID),
        tags: ['website', 'shopify-contact-form'],
        custom_fields: customFields,
      },
    };

    this.#setSubmitting(true);

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });

      if (!response.ok) {
        console.error('Zendesk contact form payload', payload);
        const errorMessage = await this.#getErrorMessage(response);
        throw new Error(errorMessage);
      }

      form.reset();
      this.#resetConditionalFields();
      this.#showSuccess(
        this.dataset.successMessage ||
          "Thanks for contacting us. We'll get back to you as soon as possible."
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.error('Zendesk contact form submission failed', error);
      this.#showError(
        error instanceof Error && error.message
          ? error.message
          : this.dataset.errorMessage ||
              'Something went wrong while submitting your enquiry. Please try again.'
      );
    } finally {
      this.#activeFetch = null;
      this.#setSubmitting(false);
    }
  };

  #resetConditionalFields() {
    const storeSelect = this.refs.storeSelect;
    if (storeSelect instanceof HTMLSelectElement) {
      storeSelect.innerHTML = '';
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.selected = true;
      placeholder.textContent = storeSelect.dataset.placeholder || 'Select a Store';
      storeSelect.append(placeholder);
    }

    const returnFieldsRow = this.refs.returnFieldsRow;
    if (returnFieldsRow instanceof HTMLElement) {
      returnFieldsRow.hidden = true;
    }
  }

  /**
   * @param {HTMLSelectElement | undefined} select
   */
  #getSelectedLabel(select) {
    if (!(select instanceof HTMLSelectElement)) return '';
    return select.selectedOptions[0]?.textContent?.trim() || '';
  }

  /**
   * @param {boolean} isSubmitting
   */
  #setSubmitting(isSubmitting) {
    const submitButton = this.refs.form?.querySelector('button[type="submit"]');
    if (!(submitButton instanceof HTMLButtonElement)) return;

    submitButton.disabled = isSubmitting;
    submitButton.setAttribute('aria-busy', String(isSubmitting));
  }

  /**
   * @param {HTMLElement} container
   * @param {string} message
   */
  #setMessageText(container, message) {
    const textNode = container.querySelector('span');
    if (textNode) {
      textNode.textContent = message;
    } else {
      container.append(message);
    }
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const errorMessage = this.refs.errorMessage;
    if (!(errorMessage instanceof HTMLElement)) return;

    this.#setMessageText(errorMessage, message);
    errorMessage.hidden = false;
    errorMessage.focus();
  }

  /**
   * @param {string} message
   */
  #showSuccess(message) {
    const successMessage = this.refs.successMessage;
    if (!(successMessage instanceof HTMLElement)) return;

    this.#setMessageText(successMessage, message);
    successMessage.hidden = false;
    successMessage.focus();
  }

  #hideMessages() {
    for (const element of [this.refs.errorMessage, this.refs.successMessage]) {
      if (!(element instanceof HTMLElement)) continue;
      element.hidden = true;
      this.#setMessageText(element, '');
    }
  }

  /**
   * @param {Response} response
   */
  async #getErrorMessage(response) {
    const fallback =
      this.dataset.errorMessage ||
      'Something went wrong while submitting your enquiry. Please try again.';

    try {
      const data = await response.json();
      const details = data?.details?.base || data?.base;

      if (Array.isArray(details)) {
        const messages = details
          .map((detail) => detail?.description || detail?.message)
          .filter(Boolean);

        if (messages.length) {
          return messages.join(' ');
        }
      }

      if (typeof data?.description === 'string' && data.description) {
        return data.description;
      }

      if (typeof data?.error === 'string' && data.error) {
        return data.error;
      }
    } catch (parseError) {
      console.error('Failed to parse Zendesk error response', parseError);
    }

    return fallback;
  }
}

if (!customElements.get('zendesk-contact-form')) {
  customElements.define('zendesk-contact-form', ZendeskContactForm);
}